"use client";

import {
  useGetMember,
  useGetMemberStucture,
} from "@/components/parts/admin/member/api";
import { Akd } from "@/components/parts/admin/member/interface";
import HeaderPage from "@/components/sections/landing/headerPage";
import MemberCard from "@/components/sections/landing/member/memberCard";
import Search from "@/components/shared/filter/search";
import SelectFilter from "@/components/shared/filter/selectFilter";
import TriggerAllFilter from "@/components/shared/filter/triggerAllFilter";
import { BreadcrumbSetItem } from "@/components/shared/layouts/myBreadcrumb";
import FieldValue from "@/components/shared/review/fieldValue";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useDapilOptions,
  useFraksiOptions,
  usePeriodOptions,
  useToolsOptions,
} from "@/hooks/useSelect";
import { useSearchParams } from "next/navigation";

const renderCommissionMembers = (commission: Akd) => {
  const leader = commission?.struktur.Ketua[0];
  const coLeaders = [
    ...(commission?.struktur.WakilKetua || []),
    ...(commission?.struktur.Sekretaris || []),
  ];
  const members = commission?.struktur.Anggota;
  return (
    <>
      <h4 className="font-semibold text-xl text-primary-700 mt-6">
        {commission.name}
      </h4>
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-5">
          {leader && (
            <MemberCard
              fractionName={leader?.fraksi}
              fractionLogo={leader?.fraksiLogo}
              slug={leader?.nameSlug}
              id={leader?.id}
              imageUrl={leader?.imageUrl}
              name={leader?.name}
              position={leader?.posisi}
              positionSlug={leader?.posisiLevelSlug}
              memberNumber={leader?.noUrut}
              dapil={leader?.dapil}
            />
          )}
          {coLeaders && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {coLeaders.map((coLeader) => (
                <MemberCard
                  key={coLeader?.id}
                  fractionName={coLeader?.fraksi}
                  fractionLogo={coLeader?.fraksiLogo}
                  slug={coLeader?.nameSlug}
                  id={coLeader?.id}
                  imageUrl={coLeader?.imageUrl}
                  name={coLeader?.name}
                  position={coLeader?.posisi}
                  positionSlug={coLeader?.posisiLevelSlug}
                  memberNumber={coLeader?.noUrut}
                  dapil={coLeader?.dapil}
                />
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {members?.map((item) => (
            <MemberCard
              key={item?.id}
              fractionName={item?.fraksi}
              fractionLogo={item?.fraksiLogo}
              slug={item?.nameSlug}
              id={item?.id}
              imageUrl={item?.imageUrl}
              name={item?.name}
              position={item?.posisi}
              positionSlug={item?.posisiLevelSlug}
              memberNumber={item?.noUrut}
              dapil={item?.dapil}
            />
          ))}
        </div>
      </div>
    </>
  );
};

const renderCommissionMemberMobile = (commission: Akd) => {
  const leader = commission?.struktur.Ketua[0];
  const coLeaders = [
    ...(commission?.struktur.WakilKetua || []),
    ...(commission?.struktur.Sekretaris || []),
  ];
  const members = commission?.struktur.Anggota;

  const allMembers = [leader, ...coLeaders, ...members];
  return (
    <AccordionItem
      value={`item-${commission.id}`}
      className="border rounded-b-xl mt-2"
    >
      <AccordionTrigger className="bg-primary-700 rounded-t-xl text-white px-2 text-lg data-[state=closed]:rounded-b-xl">
        {commission.name}
      </AccordionTrigger>
      <AccordionContent className="p-2 space-y-4">
        {allMembers.map((item) => (
          <MemberCard
            key={item?.id}
            slug={item?.nameSlug}
            fractionName={item?.fraksi}
            fractionLogo={item?.fraksiLogo}
            id={item?.id ?? 0}
            imageUrl={item?.imageUrl}
            name={item?.name}
            position={item?.posisi}
            positionSlug={item?.posisiLevelSlug}
            dapil={item?.dapil}
            memberNumber={item?.noUrut}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
};

const MemberListPage = () => {
  const isMobile = useIsMobile();
  const params = useSearchParams();
  const fractionId = params.get("fraksi");
  const dapilId = params.get("dapil");
  const fromFraction = params.get("from");

  const haveParams = fractionId !== null || dapilId !== null;
  const { data: _memberData } = useGetMemberStucture(params.toString());
  const { data: _memberList } = useGetMember(params.toString());

  const memberData = _memberData?.data;
  const electoralDistricOptions = useDapilOptions();
  const fractionOptions = useFraksiOptions();
  const akdOptions = useToolsOptions();
  const periodOptions = usePeriodOptions();

  const dprdLeader = memberData?.pimpinan.find(
    (item) => item.jabatan === "Ketua"
  );

  const dprdCoLeader = memberData?.pimpinan.filter(
    (item) => item.jabatan === "WakilKetua"
  );

  const memberList = [
    ...(memberData?.pimpinan || []),
    ...(memberData?.akd?.flatMap((commission) => [
      ...(commission.struktur.Ketua || []),
      ...(commission.struktur.WakilKetua || []),
      ...(commission.struktur.Sekretaris || []),
      ...(commission.struktur.Anggota || []),
    ]) || []),
  ];

  return (
    <section>
      <BreadcrumbSetItem
        items={[
          {
            title: "Beranda",
            href: `/`,
          },
          {
            title: "Anggota DPRD",
          },
        ]}
      />
      <HeaderPage>ANGGOTA DPRD</HeaderPage>
      <div className="sm:container px-5 md:my-10 my-6">
        <div>
          <h3 className="text-xl font-semibold text-primary-700 mb-2">
            Cari Anggota DPRD PALI
          </h3>
          <div className="flex flex-row md:flex-col-reverse gap-3 md:gap-4">
            {!fromFraction && (
              <>
                <div className="hidden md:flex gap-2 w-full">
                  <SelectFilter
                    options={periodOptions}
                    placeholder="Pilih Periode"
                    label="Periode"
                    name="periodeId"
                  />
                  <SelectFilter
                    options={electoralDistricOptions}
                    placeholder="Pilih Dapil"
                    label="Dapil"
                    name="dapil"
                  />
                  <SelectFilter
                    options={fractionOptions}
                    placeholder="Pilih Partai"
                    label="Partai"
                    name="fraksi"
                  />
                  <SelectFilter
                    options={akdOptions.slice(-4)}
                    placeholder="Pilih AKD"
                    label="AKD"
                    name="akd"
                  />
                </div>
                <div className="block md:hidden">
                  <TriggerAllFilter electoralDistrict fraction akd period />
                </div>
              </>
            )}
            <div className="flex-1">
              <Search
                placeholder="Cari Anggota"
                labelClassName="md:block hidden"
              />
            </div>
          </div>
        </div>
        <div className="md:space-y-10 mt-10">
          <div>
            <h3 className="text-xl font-semibold mb-3 text-primary-700">
              <span>Semua Anggota DPRD PALI</span>
            </h3>
            <FieldValue
              label="Menampilkan"
              variant={isMobile ? "stack" : "colon"}
              labelClassName="w-32"
              valueClassName="text-primary-700"
              className="!items-start justify-start"
            >
              <span>{_memberData?.data.total}</span>{" "}
              <span>Anggota DPRD PALI</span>
            </FieldValue>
          </div>

          {!haveParams && (
            <div>
              <div className="space-y-10 hidden md:block">
                {memberData?.pimpinan && memberData?.pimpinan.length > 0 && (
                  <div className="flex flex-col gap-5">
                    <h4 className="font-semibold text-xl text-primary-700">
                      I. PIMPINAN
                    </h4>
                    {dprdLeader && (
                      <MemberCard
                        slug={dprdLeader?.nameSlug}
                        fractionName={dprdLeader?.fraksi}
                        fractionLogo={dprdLeader?.fraksiLogo}
                        id={dprdLeader?.id ?? 0}
                        imageUrl={dprdLeader?.imageUrl}
                        name={dprdLeader?.name}
                        position={dprdLeader?.posisi}
                        positionSlug={dprdLeader?.posisiLevelSlug}
                        dapil={dprdLeader?.dapil}
                        memberNumber={dprdLeader?.noUrut}
                      />
                    )}

                    {dprdCoLeader && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {dprdCoLeader?.map((item) => (
                          <MemberCard
                            key={item?.id}
                            slug={item?.nameSlug}
                            fractionName={item?.fraksi}
                            fractionLogo={item?.fraksiLogo}
                            id={item?.id}
                            imageUrl={item?.imageUrl}
                            name={item?.name}
                            position={item?.posisi}
                            positionSlug={item?.posisiLevelSlug}
                            dapil={item?.dapil}
                            memberNumber={item?.noUrut}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {memberData?.akd && memberData?.akd.length > 0 && (
                  <div className="flex flex-col gap-5">
                    <h4 className="font-semibold text-xl text-primary-700">
                      II. KOMISI
                    </h4>
                    {memberData?.akd.map((commission) => (
                      <div key={commission.id} className="space-y-5">
                        {renderCommissionMembers(commission)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-6 md:hidden">
                <Accordion type="single" collapsible defaultValue="item-0">
                  {memberData?.pimpinan && memberData?.pimpinan.length > 0 && (
                    <AccordionItem
                      value="item-0"
                      className="border rounded-b-xl"
                    >
                      <AccordionTrigger className="bg-primary-700 rounded-t-xl text-white px-2 text-lg data-[state=closed]:rounded-b-xl">
                        PIMPINAN
                      </AccordionTrigger>
                      <AccordionContent className="p-2 space-y-4">
                        {memberData.pimpinan.map((item) => (
                          <MemberCard
                            key={item?.id}
                            slug={item?.nameSlug}
                            fractionName={item?.fraksi}
                            fractionLogo={item?.fraksiLogo}
                            id={item?.id ?? 0}
                            imageUrl={item?.imageUrl}
                            name={item?.name}
                            memberNumber={item?.noUrut}
                            position={item?.posisi}
                            positionSlug={item?.posisiLevelSlug}
                            dapil={item?.dapil}
                          />
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {memberData?.akd.map((commission) => (
                    <div key={commission.id} className="space-y-5">
                      {renderCommissionMemberMobile(commission)}
                    </div>
                  ))}
                </Accordion>

                {/* <Accordion type="single" collapsible>
                  {memberData?.akd.map((commission) => (
                    <div key={commission.id} className="space-y-5">
                      {renderCommissionMemberMobile(commission)}
                    </div>
                  ))}
                </Accordion> */}
              </div>
            </div>
          )}

          {haveParams && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              {memberList.map((item) => (
                <MemberCard
                  key={item?.id}
                  slug={item?.nameSlug}
                  fractionName={item?.fraksi}
                  fractionLogo={item?.fraksiLogo}
                  id={item?.id}
                  imageUrl={item?.imageUrl}
                  name={item?.name}
                  position={item?.posisi}
                  positionSlug={item?.posisiLevelSlug}
                  dapil={item?.dapil}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MemberListPage;
